import { html, CSSResultGroup, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import {
  OutlineElement,
  SlotsController,
  MobileController,
  emit,
  waitForEvent,
  watch,
  LightDomStyles,
} from '@phase2/outline-core';
import outlineButtonBaselineCssLit from '../outline-button-baseline/outline-button-baseline.css.lit';

import componentStyles from './outline-dropdown-baseline.css.lit';

/**
 * @element outline-dropdown-baseline
 * @since 1.0.0
 * @status experimental
 *
 * @slot header - Content to be rendered in the header of the dropdown.
 * @slot dropdown - Content to be rendered in the dropdown.
 * @slot footer - Content to be rendered in the footer of the dropdown.
 *
 * @event outline-show - Emitted when the dropdown opens.
 * @event outline-after-show - Emitted after the dropdown opens and all animations are complete.
 * @event outline-hide - Emitted when the dropdown closes.
 * @event outline-after-hide - Emitted after the dropdown closes and all animations are complete.
 *
 * @csspart base - The component's base wrapper.
 * @csspart trigger - The container that wraps the trigger.
 * @csspart panel - The panel that gets shown when the dropdown is open.
 */
type ButtonVariant = 'link' | 'primary' | 'secondary' | 'dropdown';

@customElement('outline-dropdown-baseline')
export class OutlineDropdownBaseline extends OutlineElement {
  static styles: CSSResultGroup = [
    componentStyles,
    outlineButtonBaselineCssLit,
  ];

  lightDomStyles = new LightDomStyles(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  private mobileController = new MobileController(this, 'lg');
  slots = new SlotsController(this);

  @query('.dropdown__trigger')
  trigger: HTMLElement;

  @query('.dropdown__panel')
  panel: HTMLElement;

  /**
   * Indicates whether or not the dropdown is open.
   * You can use this in lieu of the show/hide methods.
   */
  @property({ type: Boolean, reflect: true, attribute: 'is-open' })
  isOpen = false;

  /**
   * Disables the dropdown so the panel will not open.
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  isDisabled = false;

  /**
   * The dropdown will close when the user interacts outside of this element (e.g. clicking).
   */
  @property({ attribute: false })
  containingElement?: HTMLElement;

  /**
   * Visible text for the button/link of the trigger element.
   */
  @property({ type: String, attribute: 'trigger-text' })
  triggerText: string;

  /**
   * ARIA label attribute to pass down to the resulting button or a
   * element. This is required for accessibility if we use a button
   * with an icon only.
   */
  @property({ type: String, attribute: 'trigger-label' })
  triggerLabel: string;

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'trigger-variant' })
  triggerVariant: ButtonVariant = 'dropdown';

  @state() hasDropdown: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.hasDropdown = Boolean(this.slots.exist('dropdown'));

    if (!this.containingElement) {
      this.containingElement = this;
    }
  }

  updated() {
    this.style.display = this.mobileController.isMobile
      ? 'flex'
      : 'inline-flex';

    if (this.panel) {
      this.panel.hidden = !this.isOpen;
    }
  }

  disconnectedCallback() {
    this.hide();
  }

  /**
   * Shows the dropdown panel.
   */
  async show() {
    // If the dropdown is already open, do nothing.
    // If the dropdown is disabled, do nothing.
    if (this.isOpen || this.isDisabled) {
      return undefined;
    }

    if (this.panel) {
      this.panel.hidden = false;
    }

    this.isOpen = true;
    return waitForEvent(this, 'outline-after-show');
  }

  /**
   * Hides the dropdown panel.
   */
  async hide() {
    if (!this.isOpen) {
      return undefined;
    }
    if (this.panel) {
      this.panel.hidden = true;
    }
    this.isOpen = false;
    return waitForEvent(this, 'outline-after-hide');
  }

  @watch('open', { waitUntilFirstUpdate: true })
  async handleOpenChange() {
    if (this.isDisabled) {
      this.isOpen = false;
      return;
    }
    if (this.isOpen) {
      // Show the panel.
      emit(this, 'outline-show');
      emit(this, 'outline-after-show');
    } else {
      // Hide the panel.
      emit(this, 'outline-hide');
      emit(this, 'outline-after-hide');
    }
  }

  handleDocumentMouseDown(event: MouseEvent) {
    // Close when clicking outside of the containing element
    const path = event.composedPath();
    if (this.containingElement && !path.includes(this.containingElement)) {
      this.hide();
    }
  }

  handleEscKeyDown(event: KeyboardEvent) {
    // @todo: This isn't working right now.
    // Close when escape is pressed.
    if (event.key === 'Escape') {
      this.hide();
      this.focusOnTrigger();
      return;
    }
  }

  handleFocusChange() {
    setTimeout(() => {
      const activeElement =
        this.containingElement?.getRootNode() instanceof ShadowRoot
          ? document.activeElement?.shadowRoot?.activeElement
          : document.activeElement;

      if (
        !this.containingElement ||
        activeElement?.closest(this.containingElement.tagName.toLowerCase()) !==
          this.containingElement
      ) {
        this.hide();
      }
    });
  }

  handleIconClick(event: MouseEvent) {
    event.preventDefault();
    this.isOpen ? this.hide() : this.show();
  }

  handleEnterKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      if (this.isDisabled) {
        // Prevent a link from being followed.
        // Prevent the dropdown from opening.
        event.preventDefault();
        return;
      }
    }
  }

  focusOnTrigger() {
    const splitButton: HTMLElement | null = this.trigger.querySelector(
      'outline-split-button'
    );

    const regularButton: HTMLElement | null =
      this.trigger.querySelector('outline-button');

    if (splitButton && typeof splitButton?.focus === 'function') {
      splitButton?.querySelector('button')?.focus();
    }

    if (regularButton && typeof regularButton?.focus === 'function') {
      regularButton?.shadowRoot?.querySelector('button')?.focus();
    }
  }

  handlePanelKeystrokes(event: KeyboardEvent) {
    this.handleEscKeyDown(event);
    this.handleFocusChange();
  }

  handleIconTrigger(event: KeyboardEvent) {
    this.handleEnterKeyDown(event);
    this.handleEscKeyDown(event);
  }

  handleButtonTrigger(event: KeyboardEvent) {
    this.handleEnterKeyDown(event);
    this.handleEscKeyDown(event);
    this.handleFocusChange();
  }

  /**
   * @returns {TemplateResult}
   */
  render(): TemplateResult {
    return html`
      <div
        id="dropdown"
        class=${classMap({
          'dropdown': true,
          'dropdown--open': this.isOpen,
          'mobile': this.mobileController.isMobile,
        })}
      >
        <div
          class="dropdown__trigger"
          @mouseenter="${!this.mobileController.isMobile ? this.show : null}"
          @mouseleave="${this.handleFocusChange}"
        >
          ${this.buttonTemplate()} ${this.dropdownTemplate()}
        </div>
      </div>
    `;
  }

  /**
   * Template partial for the "button" rendering.
   * @returns TemplateResult | null
   */
  buttonTemplate(): TemplateResult | null {
    return html`
      <outline-button-baseline>
        <button
          class="btn ${this.triggerVariant} small ${this.isOpen
            ? 'open'
            : ''} ${this.mobileController.isMobile ? 'mobile' : ''}"
          aria-label="${ifDefined(this.triggerLabel)}"
          @keydown="${this.handleButtonTrigger}"
          @click="${this.handleIconClick}"
        >
          ${this.triggerText}
          <div class="right-icon-container">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="6 8.55 12 7.50"
              width="12"
              height="12"
            >
              <path
                d="M16.59 8.59L12 13.17L7.41 8.59L6 10l6 6l6-6l-1.41-1.41z"
              ></path>
            </svg>
          </div>
        </button>
      </outline-button-baseline>
    `;
  }

  /**
   * Template partial for the dropdown rendering.
   * @returns TemplateResult | null
   */
  dropdownTemplate(): TemplateResult | null {
    if (!this.hasDropdown) return null;

    return html`
      <div
        class="dropdown__panel"
        aria-hidden=${this.isOpen ? 'false' : 'true'}
        aria-labelledby="dropdown"
        @keydown="${this.handlePanelKeystrokes}"
      >
        <slot name="dropdown"></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-dropdown-baseline': OutlineDropdownBaseline;
  }
}
